import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Check, Columns3 } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import {
    Command, CommandGroup, CommandInput, CommandItem, CommandList,
} from '../ui/command';
import CustomFieldBadge from '../custom-fields/CustomFieldBadge';
import SourceColumnBadge from '../custom-fields/SourceColumnBadge';
import { GhostButton, LmTip } from './LmBits';

/**
 * The List Management "Columns" control (#974, owner decisions D2 and D3): a
 * searchable multiselect over every column the tab can show, grouped
 * "In these imports" → "State" → "Other CRM fields". Presentational: the page
 * owns the visibility model (lmColumns.js) and passes the resulting
 * `visibleIds` and the four callbacks.
 */
export const COLUMN_GROUPS = [
    { key: 'imports', heading: 'IN THESE IMPORTS' },
    { key: 'state', heading: 'STATE' },
    { key: 'other', heading: 'OTHER CRM FIELDS' },
];

const matches = (c, q) => {
    if (!q) return true;
    const needle = q.toLowerCase();
    return c.label.toLowerCase().includes(needle) || (c.header || '').toLowerCase().includes(needle);
};

const NAV_KEYS = new Set(['ArrowDown', 'ArrowUp', 'Home', 'End', 'PageDown', 'PageUp']);
const HOVER_DELAY = 300;   // the page's TooltipProvider delayDuration

function Option({ column, on, onToggle, tipFor, onTipClose, onHover }) {
    // #975 (P4): cmdk keeps DOM focus in the search input, so a focus-driven
    // tooltip never fires on an option, and cmdk's item replaces the trigger's
    // onPointerMove, so Radix never sees a hover either. The tooltip is
    // controlled instead: it shows for the option the picker's `tipFor` names,
    // set by hovering the option (onPointerEnter / onPointerLeave) or by an
    // arrow-key highlight. The Custom / From file badges inside an option get no
    // separate tooltip: the option's description already names the origin. The
    // always-shown option is disabled for cmdk but keeps pointer events, so
    // hovering it still shows its description.
    const open = tipFor === column.id;
    return (
        <LmTip description={column.description} side="right" open={open} onOpenChange={(next) => { if (!next) onTipClose(column.id); }}>
            <CommandItem
                value={column.id} disabled={column.primary}
                onSelect={() => onToggle(column, !on)}
                role="option" aria-checked={on} aria-disabled={column.primary || undefined}
                data-testid="lm-column-option" data-column-id={column.id} data-on={on ? 'true' : 'false'}
                onPointerEnter={() => onHover(column.id, true)} onPointerLeave={() => onHover(column.id, false)}
                className="gap-2.5 px-2.5 py-1.5 text-[12.5px] text-foreground data-[disabled=true]:pointer-events-auto"
            >
                <span aria-hidden="true"
                      className={`flex items-center justify-center w-4 h-4 shrink-0 rounded border ${on ? 'bg-primary border-primary text-primary-foreground' : 'border-muted-foreground/80'}`}>
                    {on && <Check className="w-3 h-3" />}
                </span>
                <span className="min-w-0 flex-1 truncate">{column.label}</span>
                {column.primary ? (
                    <span className="shrink-0 text-[11px] text-ink-2">Always shown</span>
                ) : (
                    <span className="shrink-0 inline-flex items-center gap-1.5 text-[11px] text-ink-2">
                        {!column.isFile && column.group !== 'state' && <span>{column.kindLabel}</span>}
                        {column.isCustom && <CustomFieldBadge />}
                        {column.isFile && <SourceColumnBadge />}
                    </span>
                )}
            </CommandItem>
        </LmTip>
    );
}

export default function LmColumnPicker({ columns, visibleIds, onToggle, onSelectAll, onClear, onReset }) {
    const [query, setQuery] = useState('');
    const [active, setActive] = useState('');
    const [tipFor, setTipFor] = useState('');
    // cmdk's controlled `value` is the highlighted option. It also moves when
    // the list mounts, filters, or the pointer passes over an option, so the
    // tooltip follows it only when an arrow key moved it; hover is tracked
    // below (onHover).
    const arrowedRef = useRef(false);
    const onKeyDown = (e) => { arrowedRef.current = NAV_KEYS.has(e.key); };
    const onValueChange = (next) => {
        setActive(next);
        if (arrowedRef.current) setTipFor(next);
        arrowedRef.current = false;
    };
    // Hover waits the provider's delay, then switches straight to the next option
    // while one is shown, as the table's tooltips do.
    const hoverTimer = useRef(0);
    const shownRef = useRef('');
    shownRef.current = tipFor;
    useEffect(() => () => clearTimeout(hoverTimer.current), []);
    // Esc, a scroll or the pointer leaving closes a tooltip, but only its own: a
    // scroll that closes the option the pointer just left must not clear the one
    // it moved to, nor must leaving an option clear an arrowed-to one.
    const onTipClose = (id) => setTipFor((cur) => (cur === id ? '' : cur));
    const onHover = (id, entering) => {
        clearTimeout(hoverTimer.current);
        if (!entering) onTipClose(id);
        else if (shownRef.current) setTipFor(id);
        else hoverTimer.current = setTimeout(() => setTipFor(id), HOVER_DELAY);
    };
    const onOpenChange = (next) => {
        if (next) return;
        clearTimeout(hoverTimer.current);
        setQuery(''); setActive(''); setTipFor(''); arrowedRef.current = false;
    };
    const q = query.trim();
    const groups = useMemo(() => COLUMN_GROUPS.map((g) => {
        const all = columns.filter((c) => c.group === g.key);
        return { ...g, all, shown: all.filter((c) => matches(c, q)), on: all.filter((c) => visibleIds.has(c.id)).length };
    }).filter((g) => g.all.length > 0), [columns, visibleIds, q]);
    const matching = groups.reduce((n, g) => n + g.shown.length, 0);

    return (
        <Popover onOpenChange={onOpenChange}>
            <PopoverTrigger asChild>
                <GhostButton icon={Columns3} testId="lm-columns">
                    Columns · {visibleIds.size} of {columns.length}
                </GhostButton>
            </PopoverTrigger>
            <PopoverContent align="start" className="w-[360px] max-w-[calc(100vw-24px)] p-0" data-testid="lm-columns-panel">
                <Command shouldFilter={false} loop value={active} onValueChange={onValueChange} onKeyDown={onKeyDown} className="bg-popover text-popover-foreground">
                    <CommandInput value={query} onValueChange={setQuery} placeholder="Search columns..."
                                  aria-label="Search columns" data-testid="lm-columns-search" />
                    <div className="flex items-center justify-between gap-2 px-3 py-2 border-b border-border text-[12px] text-muted-foreground">
                        <span role="status" data-testid="lm-columns-count">{visibleIds.size} of {columns.length} shown</span>
                        <span className="inline-flex items-center gap-2.5">
                            <button type="button" onClick={onSelectAll} data-testid="lm-columns-select-all"
                                    className="font-medium text-foreground hover:underline">Select all</button>
                            <button type="button" onClick={onClear} data-testid="lm-columns-clear"
                                    className="font-medium text-foreground hover:underline">Clear</button>
                            <button type="button" onClick={onReset} data-testid="lm-columns-reset"
                                    className="font-medium text-foreground hover:underline">Reset to default</button>
                        </span>
                    </div>
                    <CommandList aria-label="Columns" aria-multiselectable="true" className="max-h-[320px] p-1">
                        {groups.filter((g) => g.shown.length > 0).map((g) => (
                            <CommandGroup
                                key={g.key} data-testid={`lm-columns-group-${g.key}`}
                                heading={(
                                    <span className="flex items-center justify-between">
                                        <span className="tracking-[0.04em]">{g.heading}</span>
                                        <span className="tabular-nums" data-testid={`lm-columns-group-${g.key}-count`}>{g.on} of {g.all.length}</span>
                                    </span>
                                )}
                            >
                                {g.shown.map((c) => (
                                    <Option key={c.id} column={c} on={visibleIds.has(c.id)} onToggle={onToggle}
                                            tipFor={tipFor} onTipClose={onTipClose} onHover={onHover} />
                                ))}
                            </CommandGroup>
                        ))}
                    </CommandList>
                    {matching === 0 && (
                        <div className="px-3 py-6 text-center text-[12.5px] text-muted-foreground" role="status" data-testid="lm-columns-empty">
                            No columns match “{q}”
                        </div>
                    )}
                    <div className="px-3 py-2 border-t border-border text-[11.5px] text-muted-foreground">
                        Columns your imports carry are on by default. Your choices are remembered for this tab.
                    </div>
                </Command>
            </PopoverContent>
        </Popover>
    );
}
